'use client';

import { APILoadingStatus, AdvancedMarker, Map, useApiLoadingStatus, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useMemo, memo, Suspense, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/lib/hooks';
import { setBounds, setViewport, setLoading, setError, setSelectedHotel, getMapData } from '@/lib/features/map/mapSlice';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import debounce from 'lodash-es/debounce';
import HotelPopup from './HotelPopup';
import { isEqual } from 'lodash-es';
import { useRouter, useSearchParams } from 'next/navigation';
import { makeQueryString, parseQueryString } from '@/utils/queryString';
import { loading } from '@/lib/features/hotel/hotelSlice';
import {
  getHotels,
  getSearchPayload,
  hotels as AllHotels,
  loadMoreHotel,
  setCurrentBatchIndex,
  setSelectedBatch,
  getLoadingMore,
  saveSearchPayload,
  getHotelsProgress,
  Filter,
} from "@/lib/features/hotel/hotelSlice";

interface Hotel {
  [key: string | number]: any;
  name: string;
  price?: number | string;
}

interface HotelsMapProps {
  hotels?: any;
  initialLocation?: google.maps.LatLngLiteral;
}

const HotelsMap = ({ hotels, initialLocation }: HotelsMapProps) => {
  const status = useApiLoadingStatus();
  const dispatch = useAppDispatch();
  const { viewport, bounds, selectedHotel, error } = useAppSelector(getMapData);

  const mapId = `roave-map`;
  const map = useMap(mapId);
  const clusterer = useRef<MarkerClusterer | null>(null);
  const markers = useRef<{ [key: string]: google.maps.marker.AdvancedMarkerElement }>({});
  const markersLibrary = useMapsLibrary('marker');
  const [showSearchButton, setShowSearchButton] = useState<boolean>(false);
  const [mapLoading, setMapLoading] = useState<boolean>(true);
  const [locationName, setLocationName] = useState<string | null>(null);
  const queryParams: any = useSearchParams();
  const router = useRouter();

  const loadingHotels = useAppSelector(loading);

  
  const memoizedInitialLocation = useMemo(() => initialLocation, [initialLocation]);
  const isInitialMount = useRef(true);

  const payloadFromQuery: any = useMemo(() => {
    if (!queryParams) return null;
    return parseQueryString(queryParams);
  }, [queryParams]);

  // Set initial viewport only on first mount
  useEffect(() => {
    if (isInitialMount.current && memoizedInitialLocation) {
      dispatch(setViewport({
        center: memoizedInitialLocation,
        zoom: 12,
      }));
      isInitialMount.current = false;
    }
  }, [memoizedInitialLocation, dispatch]);

  // Handle resize to preserve map position
  useEffect(() => {
    const handleResize = () => {
      if (map && viewport) {
        map.setCenter(viewport.center);
        map.setZoom(viewport.zoom);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [map, viewport]);

  // Debounced click handler
  const handleMarkerClick = useCallback(
    debounce((hotel: any, hotelInfo: any) => {
      console.log(`Debounced marker click: ${hotel.hotel_code}`);
      dispatch(setSelectedHotel({ hotel, hotelInfo }));
    }, 300),
    [dispatch]
  );

  // Debounced bounds update
  const updateBounds = useCallback(
    debounce((mapInstance: google.maps.Map) => {
      const currentBounds = mapInstance.getBounds();
      if (currentBounds) {
        console.log("currentBounds=>><<", currentBounds)
        const newBounds = {
          east: currentBounds.getNorthEast().lng(),
          north: currentBounds.getNorthEast().lat(),
          west: currentBounds.getSouthWest().lng(),
          south: currentBounds.getSouthWest().lat(),
        };
        if (!isEqual(newBounds, bounds)) {
          dispatch(setBounds(newBounds));
          setShowSearchButton(true);
        }
      }
    }, 300),
    [dispatch, bounds]
  );

  // Handle map bounds change
  const handleBoundsChanged = useCallback(() => {
    if (!map) return;

    const center = map.getCenter();
    if (!center) return;

    const newViewport = {
      center: { lat: center.lat(), lng: center.lng() },
      zoom: map.getZoom() || 12,
    };

    if (!isEqual(newViewport, viewport)) {
      dispatch(setViewport(newViewport));
    }

    updateBounds(map);
  }, [map, dispatch, updateBounds, viewport]);

  // Reverse geocode to get location name from coordinates
  const getLocationNameFromBounds = useCallback(async (lat: number, lng: number) => {
    if (!window.google) return 'Unknown Location';
    const geocoder = new window.google.maps.Geocoder();
    try {
      const response = await geocoder.geocode({ location: { lat, lng } });
      if (response.results.length > 0) {
        const result = response.results.find((res: any) =>
          res.types.includes('locality') || res.types.includes('political') || res.types.includes('sublocality')
        ) || response.results[0];
        return result.formatted_address || 'Unknown Location';
      }
      return 'Unknown Location';
    } catch (error) {
      console.error('Error fetching location name:', error);
      return 'Unknown Location';
    }
  }, []);


  // Handle "Search this area"
  const handleSearchArea = useCallback(async() => {
    if (!bounds) return;
    const newQueryString = makeQueryString({ ...payloadFromQuery, ...bounds, radius: 5000, geolocation: true });
    setShowSearchButton(false);
    
    // Update viewport to center of new bounds
    const center = {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2,
    };
    // Get the location name for the center
    const location = await getLocationNameFromBounds(center.lat, center.lng);
    setLocationName(location); // Store the location name in state
    console.log('Current location name:', location);
    // console.log("bounds=>><<", bounds)
    dispatch(setViewport({ center, zoom: 12 }));
    
    router.push(`/hotels?${newQueryString}`);
  }, [dispatch, bounds, payloadFromQuery, router, getLocationNameFromBounds]);

  const buildMarkerContent = useCallback((hotel: Hotel) => {
    const element = document.createElement('div');
    element.className = 'hotel-marker position-relative';
    element.title = hotel.name;

    element.innerHTML = `
      <div style="position: relative; text-align: center;"
      onmouseover="this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.8)';"
      onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.6)';"
      >
        <div style="
          position: absolute;
          top: -24px;
          left: 50%;
          transform: translateX(-50%);
          background: #032d43;
          color: #FFF;
          padding: 2px 6px;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          font-size: 12px;
          white-space: nowrap;
          z-index: 1;
          font-weight: bold;
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        ">
          ${hotel?.min_rate?.currency} ${hotel?.min_rate?.priceWithMarkup || 'N/A'}
        </div>
        <div style="
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #032d43;
          filter: drop-shadow(0 1px 1px rgba(0,0,0,0.2));
          z-index: 1;
        "></div>
      </div>
    `;
    return element;
  }, []);

  const buildClusterContent = useCallback((count: number) => {
    const element = document.createElement('div');
    element.className = 'cluster-marker';

    element.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#032d43" stroke="#fff" stroke-width="2"/>
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#fff" font-size="16" font-weight="bold">${count}</text>
      </svg>
    `;
    return element;
  }, []);

  useEffect(() => {
    if (!map || !markersLibrary || clusterer.current) return;
    clusterer.current = new MarkerClusterer({
      map,
      markers: [],
      renderer: {
        render: ({ count, position }) => {
          const clusterMarker = new markersLibrary.AdvancedMarkerElement({
            position,
            content: buildClusterContent(count),
          });
          clusterMarker.addListener('click', () => {
            map.setZoom(map.getZoom()! + 2);
            map.panTo(position);
          });
          return clusterMarker;
        },
      },
    });

    return () => {
      clusterer.current?.clearMarkers();
      clusterer.current = null;
    };
  }, [map, markersLibrary]);

  const hotelMarkersData = useMemo(() => {
    return hotels || [];
  }, [hotels]);

  useEffect(() => {
    if (status !== APILoadingStatus.LOADED || !map || !clusterer.current || !markersLibrary) {
      setMapLoading(true);
      return;
    }

    if (!hotelMarkersData?.hotels?.length) {
      clusterer.current.clearMarkers();
      markers.current = {};
      setMapLoading(false);
      return;
    }

    clusterer.current.clearMarkers();
    Object.values(markers.current).forEach((marker) => {
      google.maps.event.clearInstanceListeners(marker);
    });
    markers.current = {};

    const {
      no_of_nights,
      no_of_rooms,
      no_of_adults,
      no_of_children,
      no_of_infants,
    } = hotelMarkersData;
    const hotelInfo = {
      no_of_nights,
      no_of_rooms,
      no_of_adults,
      no_of_children,
      no_of_infants,
    };
    const newMarkers = hotelMarkersData?.hotels?.map((hotel: any) => {
      const hotelId = Number(hotel?.hotel_code);
      const marker = new markersLibrary.AdvancedMarkerElement({
        position: {
          lat: hotel.geolocation.latitude,
          lng: hotel.geolocation.longitude,
        },
        map,
        content: buildMarkerContent(hotel),
      });

      let isTouchEvent = false;

      // marker.addListener('click', () => {
      //   const info = { hotel, hotelInfo };
      //   console.log("info", info);
      //   dispatch(setSelectedHotel(info));
      // });

      // Touch handler
      marker.addListener('touchend', (event: google.maps.MapMouseEvent) => {
        console.log(`Touchend on marker: ${hotelId}`);
        isTouchEvent = true;
        handleMarkerClick(hotel, hotelInfo);
        event.stop();
      });

      // Click handler
      marker.addListener('click', () => {
        console.log(`Click event fired for marker: ${hotelId}`);
        if (isTouchEvent) {
          console.log(`Ignoring click after touch: ${hotelId}`);
          isTouchEvent = false;
          return;
        }
        handleMarkerClick(hotel, hotelInfo);
      });


      markers.current[hotelId] = marker;
      return marker;
    });

    clusterer.current.addMarkers(newMarkers);

    if (newMarkers.length) {
      const bounds = new google.maps.LatLngBounds();
      newMarkers.forEach((marker: any) => {
        bounds.extend(marker.position);
      });
      map.fitBounds(bounds);
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom();
        if (currentZoom !== undefined && currentZoom > 15) {
          map.setZoom(12);
        }
        setMapLoading(false);
      });
    } else {
      setMapLoading(false);
    }

    return () => {
      newMarkers.forEach((marker: any) => {
        google.maps.event.clearInstanceListeners(marker);
      });
      clusterer.current?.clearMarkers();
      markers.current = {};
    };
  }, [hotelMarkersData, map, markersLibrary, dispatch, buildMarkerContent, handleMarkerClick]);

  const getPopupPosition = useCallback((hotel: Hotel) => {
    if (!map || !hotel.geolocation) return { left: '50%', top: '50%' };

    const projection = map.getProjection();
    const mapDiv = map.getDiv();

    if (!projection || !mapDiv) return { left: '50%', top: '50%' };

    const scale = Math.pow(2, map.getZoom() || 12);
    const bounds = map.getBounds();
    if (!bounds) return { left: '50%', top: '50%' };

    const nw = projection.fromLatLngToPoint(bounds.getNorthEast());
    const se = projection.fromLatLngToPoint(bounds.getSouthWest());
    const worldPoint = projection.fromLatLngToPoint(
      new google.maps.LatLng(hotel.geolocation.latitude, hotel.geolocation.longitude)
    );

    if (!nw || !se || !worldPoint) return { left: '50%', top: '50%' };

    const x = (worldPoint.x - se.x) * scale;
    const y = (worldPoint.y - nw.y) * scale;

    return {
      left: `${x}px`,
      top: `${y}px`,
    };
  }, [map]);

  const id = payloadFromQuery && payloadFromQuery?.location?.code;
  const hotelsData = useAppSelector(AllHotels);

  const allHotels = hotelsData && hotelsData[id];


  useEffect(() => {
    if(allHotels){
      console.log('all hotels in maps after changes', allHotels)
    }
  },[allHotels])

  
  return (
    <>
      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <div className="position-relative" style={{ height: '700px', width: '100%' }}>
          {/* {JSON.stringify(selectedHotel)} */}
          {/* {locationName && (
            <div
              style={{
                position: 'absolute',
                top: '50px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: 'white',
                padding: '5px 10px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Current Location: {locationName}
            </div>
          )} */}

          {status === APILoadingStatus.LOADED ? (
            <Map
              id="roave-map"
              mapId={`${process.env.NEXT_PUBLIC_GOOGLE_MAP_ID}`}
              center={viewport?.center || memoizedInitialLocation}
              zoom={viewport?.zoom || 12}
              gestureHandling="greedy"
              disableDefaultUI={true}
              onBoundsChanged={handleBoundsChanged}
              onIdle={handleBoundsChanged}
            >
              {selectedHotel && (
                <div
                  style={{
                    position: 'absolute',
                    height: "100vh",
                    ...getPopupPosition(selectedHotel.hotel),
                    zIndex: 1050,
                  }}
                >
                  <HotelPopup
                    hotelItem={selectedHotel}
                    onClose={() => dispatch(setSelectedHotel(null))}
                  />
                </div>
              )}
            </Map>
          ) : (
            <div className="text-center py-5 text-muted">
              Map Loading... (Status: {status})
            </div>
          )}

          {showSearchButton && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
              }}
            >
              <button
                onClick={handleSearchArea}
                className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
              >
                Search this area
              </button>
            </div>
          )}
         
          {(mapLoading || loadingHotels ) && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25">
              <div className="bg-white p-3 rounded shadow d-flex align-items-center gap-2">
                <div className="spinner-border -dark-1" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span>Loading hotels...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25">
              <div className="bg-white p-3 rounded shadow text-center">
                <h3 className="text-danger mb-2">Error</h3>
                <p className="text-muted mb-3">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
                >
                  Reload Map
                </button>
              </div>
            </div>
          )}
        </div>
      </Suspense>
    </>
  );
};

export default memo(HotelsMap);
/*'use client';

import { APILoadingStatus, AdvancedMarker, Map, useApiLoadingStatus, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useMemo, memo, Suspense, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/lib/hooks';
import { setBounds, setViewport, setLoading, setError, setSelectedHotel, getMapData } from '@/lib/features/map/mapSlice';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import debounce from 'lodash-es/debounce';
import HotelPopup from './HotelPopup';
import { isEqual } from 'lodash-es';
import { useRouter, useSearchParams } from 'next/navigation';
import { makeQueryString, parseQueryString } from '@/utils/queryString';

interface Hotel {
  [key: string | number]: any;
  name: string;
  price?: number | string; // Assuming hotel object has a price field
}

interface HotelsMapProps {
  hotels?: any;
  initialLocation?: google.maps.LatLngLiteral;
}

const HotelsMap = ({ hotels, initialLocation }: HotelsMapProps) => {

  const status = useApiLoadingStatus();
  const dispatch = useAppDispatch();
  const { viewport, bounds, selectedHotel, loading, error } = useAppSelector(getMapData);

  const mapId = `roave-map`;
  const map = useMap(mapId);
  const clusterer = useRef<MarkerClusterer | null>(null);
  const markers = useRef<{ [key: string]: google.maps.marker.AdvancedMarkerElement }>({});
  const markersLibrary = useMapsLibrary('marker');
  // State to show/hide the "Search this area" button
  const [showSearchButton, setShowSearchButton] = useState<boolean>(false);
  const [mapLoading, setMapLoading] = useState<boolean>(true);
  const queryParams: any = useSearchParams();
  const router = useRouter();
  // Memoized initial location
  const memoizedInitialLocation = useMemo(() => initialLocation, [initialLocation]);

   // Parse the query string into a payload using parseQueryString
   const payloadFromQuery: any = useMemo(() => {
    if (!queryParams) return null; // Handle case where searchParams isn't ready
    return parseQueryString(queryParams);
  }, [queryParams]);

  // Reset mapLoading to true when the component mounts
  

  // Set initial viewport with initialLocation
  useEffect(() => {
    if (memoizedInitialLocation) {
      dispatch(setViewport({
        center: memoizedInitialLocation,
        zoom: 12,
      }));
    }
  }, [memoizedInitialLocation, dispatch]);

  // Debounced bounds update function
  const updateBounds = useCallback(
    debounce((mapInstance: google.maps.Map) => {
      const currentBounds = mapInstance.getBounds();
      if (currentBounds) {
        const newBounds = {
          east: currentBounds.getNorthEast().lng(),
          north: currentBounds.getNorthEast().lat(),
          west: currentBounds.getSouthWest().lng(),
          south: currentBounds.getSouthWest().lat(),
        };
        if (!isEqual(newBounds, bounds)) {
        // if (JSON.stringify(newBounds) !== JSON.stringify(bounds)) {
          console.log('location changes', newBounds, bounds);
          dispatch(setBounds(newBounds));
          setShowSearchButton(!showSearchButton);
        }
      }
    }, 300),
    [dispatch, bounds]
  );

  // Handle map bounds change
  const handleBoundsChanged = useCallback(() => {
    if (!map) return;

    const center = map.getCenter();
    if (!center) return;

    const newViewport = {
      center: { lat: center.lat(), lng: center.lng() },
      zoom: map.getZoom() || 12,
    };

    if (!isEqual(newViewport, viewport)) {
      dispatch(setViewport(newViewport));
    }

    updateBounds(map);
  }, [map, dispatch, updateBounds, viewport]);

  

  // Handle "Search this area" button click
  const handleSearchArea = useCallback(() => {
    if (!bounds) return;
    const newQueryString = makeQueryString({ ...payloadFromQuery, ...bounds, radius: 5000, geolocation: true });
    setShowSearchButton(false); // Hide button after search
    router.push(`/hotels?${newQueryString}`);
  }, [dispatch, bounds, payloadFromQuery, router]);


  const buildMarkerContent = useCallback((hotel: Hotel) => {
    const element = document.createElement('div');
    element.className = 'hotel-marker position-relative';
    element.title = hotel.name;

    element.innerHTML = `
      <div style="position: relative; text-align: center;"
      onmouseover="this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.8)';"
      onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.6)';"
      >
        <!-- Price tooltip -->
        <div style="
          position: absolute;
          top: -24px;
          left: 50%;
          transform: translateX(-50%);
          background: #032843;
          color: #FFF;
          padding: 2px 6px;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          font-size: 12px;
          white-space: nowrap;
          z-index: 1;
          font-weight: bold;
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        "
       
        >
          ${hotel?.min_rate?.currency} ${hotel?.min_rate?.priceWithMarkup || 'N/A'}
        </div>
        
        <!-- Arrow pointing down to marker -->
        <div style="
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #032843; 
          filter: drop-shadow(0 1px 1px rgba(0,0,0,0.2));
          z-index: 1;
        "></div>
        
       
      </div>
    `;
    // <svg 
    //   xmlns="http://www.w3.org/2000/svg" 
    //   width="32" 
    //   height="32" 
    //   viewBox="0 0 24 24" 
    //   fill="none" 
    //   stroke="#dc3545" 
    //   stroke-width="2" 
    //   stroke-linecap="round" 
    //   stroke-linejoin="round"
    // >
    //   <path d="M12 2a10 10 0 0 1 10 10c0 5-10 10-10 10S2 17 2 12A10 10 0 0 1 12 2z"/>
    //   <path d="M8 10h8v4H8z"/>
    //   <rect x="10" y="8" width="4" height="2"/>
    // </svg>
    return element;
}, []);


  // Custom SVG Icon for Cluster
  const buildClusterContent = useCallback((count: number) => {
    const element = document.createElement('div');
    element.className = 'cluster-marker';

    element.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#032843" stroke="#fff" stroke-width="2"/>
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#fff" font-size="16" font-weight="bold">${count}</text>
      </svg>
    `;

    return element;
  }, []);

  // Initialize clusterer once
  useEffect(() => {
    if (!map || !markersLibrary || clusterer.current) return;
    clusterer.current = new MarkerClusterer({
        map,
        markers: [],
        renderer: {
          render: ({ count, position }) => {
            const clusterMarker = new markersLibrary.AdvancedMarkerElement({
              position,
              content: buildClusterContent(count),
            });
            clusterMarker.addListener('click', () => {
              // console.log("Cluster clicked, count:", count);
              // Optionally zoom in or handle cluster click
              map.setZoom(map.getZoom()! + 2);
              map.panTo(position);
            });
            return clusterMarker;
          },
        },
      });

    return () => {
      clusterer.current?.clearMarkers();
      clusterer.current = null;
    };
  }, [map, markersLibrary]);

  // Memoize hotel markers data
  const hotelMarkersData = useMemo(() => {
    return hotels || [];
  }, [hotels]);
  // Update markers and fit bounds to hotel locations
  useEffect(() => {
    if (status !== APILoadingStatus.LOADED || !map || !clusterer.current || !markersLibrary) {
      setMapLoading(true); // Show loader when starting to process markers
      return;
    }

    if (!hotelMarkersData?.hotels?.length) {
      // setMapLoading(false); // Hide loader if no hotels
      return;
    }

    
    // console.log("Updating markers:", { hotelMarkersData });

    clusterer.current.clearMarkers();
    markers.current = {};

    const {
      no_of_nights,
      no_of_rooms,
      no_of_adults,
      no_of_children,
      no_of_infants,
    } = hotelMarkersData;
    const hotelInfo = {
      no_of_nights,
      no_of_rooms,
      no_of_adults,
      no_of_children,
      no_of_infants,
    };
    const newMarkers = hotelMarkersData?.hotels?.map((hotel: any) => {
      const hotelId = Number(hotel?.hotel_code);
      const marker = new markersLibrary.AdvancedMarkerElement({
        position: {
          lat: hotel.geolocation.latitude,
          lng: hotel.geolocation.longitude,
        },
        map,
        content: buildMarkerContent(hotel),
      });

      // Fix click event
      marker.addListener('click', () => {
        const info = { hotel, hotelInfo}
        dispatch(setSelectedHotel(info));
      });

      markers.current[hotelId] = marker;
      return marker;
    });

    clusterer.current.addMarkers(newMarkers);

    // Fit map to hotel bounds
    if (newMarkers.length) {
      const bounds = new google.maps.LatLngBounds();
      newMarkers.forEach((marker: any) => {
        bounds.extend(marker.position);
      });
      map.fitBounds(bounds);
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom();
        if (currentZoom !== undefined && currentZoom > 15) {
          map.setZoom(12);
        }
        // setMapLoading(false); // Hide loader once bounds are set and markers are added
      });
    }
    else{
      // setMapLoading(false); // Hide loader if no markers
    }

    setTimeout(() => {
      setMapLoading(false); // Hide loader after a delay
    }, 1000);
    return () => {
      newMarkers.forEach((marker: any) => {
        google.maps.event.clearInstanceListeners(marker);
      });
    };
  }, [hotelMarkersData, map, markersLibrary, dispatch, status]);

  // Convert lat/lng to pixel position for popup
const getPopupPosition = useCallback((hotel: Hotel) => {
  if (!map || !hotel.geolocation) return { left: '50%', top: '50%' };

  const projection = map.getProjection();
  const mapDiv = map.getDiv();

  if (!projection || !mapDiv) return { left: '50%', top: '50%' };

  const scale = Math.pow(2, map.getZoom() || 12);
  const bounds = map.getBounds();
  if (!bounds) return { left: '50%', top: '50%' };

  const nw = projection.fromLatLngToPoint(bounds.getNorthEast());
  const se = projection.fromLatLngToPoint(bounds.getSouthWest());
  const worldPoint = projection.fromLatLngToPoint(
    new google.maps.LatLng(hotel.geolocation.latitude, hotel.geolocation.longitude)
  );

  if (!nw || !se || !worldPoint) return { left: '50%', top: '50%' };

  const x = (worldPoint.x - se.x) * scale;
  const y = (worldPoint.y - nw.y) * scale;

  return {
    left: `${x}px`,
    top: `${y}px`,
  };
}, [map]);


  return (
    <>
      <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
        <div className="position-relative" style={{ height: '700px', width: '100%' }}>
          {status === APILoadingStatus.LOADED ? (
            <Map
              id="roave-map"
              mapId={`${process.env.NEXT_PUBLIC_GOOGLE_MAP_ID}`}
              center={viewport?.center}
              zoom={viewport?.zoom}
              gestureHandling="greedy"
              disableDefaultUI={true}
              onBoundsChanged={handleBoundsChanged}
              onIdle={handleBoundsChanged}
            >
             {selectedHotel && (
            <div
              style={{
                position: 'absolute',
                // ...getPopupPosition(selectedHotel),
                zIndex: 1050,
              }}
            >
              <HotelPopup
                hotelItem={selectedHotel}
                onClose={() => dispatch(setSelectedHotel(null))}
              />
            </div>
          )}
            </Map>
          ) : (
            <div className="text-center py-5 text-muted">
              Map Loading... (Status: {status})
            </div>
          )}


{showSearchButton && (
            <div
              style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
              }}
            >
              <button
                onClick={handleSearchArea}
                className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
              >
                Search this area
              </button>
            </div>
          )}
  
          {mapLoading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25">
              <div className="bg-white p-3 rounded shadow d-flex align-items-center gap-2">
                <div className="spinner-border -dark-1" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span>Loading hotels...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25">
              <div className="bg-white p-3 rounded shadow text-center">
                <h3 className="text-danger mb-2">Error</h3>
                <p className="text-muted mb-3">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
                >
                  Reload Map
                </button>
              </div>
            </div>
          )}
        </div>
      </Suspense>
    </>
  );
};

export default memo(HotelsMap);*/